import { QueryTermType, SchemaLevel } from "#src/core/backend/controllers";

type Paths<T> = T extends object ? { [K in keyof T]: `${Exclude<K, symbol>}${"" | `.${Paths<T[K]>}`}` }[keyof T] : never;

export type Translation = typeof i18n_de;
export type TranslationPathArray = Paths<Translation>;

export const SchemaLevels: Record<SchemaLevel, string> = {
  [SchemaLevel.Archive]: "Archive",
  [SchemaLevel.WebPages]: "WebPages",
  [SchemaLevel.WebPage]: "WebPage",
  [SchemaLevel.Organization]: "Organization",
  [SchemaLevel.Publication]: "Publication",
  [SchemaLevel.Level]: "Level",
  [SchemaLevel.Document]: "Document",
  [SchemaLevel.Chapter]: "Chapter",
};

export const i18n_de = {
  ...SchemaLevels,
  PAGE_NOT_FOUND: "Seite nicht gefunden",
  NO_DOCUMENT_OR_CONTENT: "Kein Dokument oder Inhalt gefunden!",
  LANGUAGES: {
    THIS: "Deutsch",
    FR: "Français",
    DE: "Deutsch",
    EN: "English",
  },
  LOGOUT: {
    DIALOG: {
      TITLE: "Abmelden",
      CONTENT: "Sind Sie sicher, dass sich Sie abmelden wollen?",
      CANCEL: "Abbrechen",
      OK: "Ja",
    },
  },
  GDPR: {
    MESSAGE:
      "Um unsere Webseite für Sie optimal zu gestalten und fortlaufend verbessern zu können, verwenden wir Cookies. Durch die weitere Nutzung der Webseite stimmen Sie der Verwendung von Cookies zu.",
    BTN_ACCEPT: "Ich akzeptiere",
  },
  TOPNAV: {
    TITLE: "Retrodigital",
    SUBTITLE: "Kanton St.Gallen",
    BTN_LOGOUT: "Abmelden",
    BTN_ADMIN: "Admin Panel",
  },
  AUTOCOMPLETE: {
    ADD_TAG: "Eintrag hinzufügen",
    NOT_FOUND: "Keine Resultate",
    CLEAR_ALL: "Alles löschen",
    LOADING: "Lade...",
    TYPE_TO_SEARCH: "Tippen, um Vorschläge zu sehen...",
  },
  SEARCH: {
    TITLE: "Suche",
    CLEAR_FILTERS: "Filter zurücksetzen",
    START_SEARCH: "Suche",
    BTN_SEARCH: "Suchen",
    TYPE_TO_SEARCH: "Eingabe für Suche",
    FIELD_QUICK_SEARCH: "Suchen",
    FIELD_DOCUMENTTYPE: "Dokumententyp",
    FIELD_COMMUNE: "Gemeinde",
    FIELD_SUFFIX_VINTAGE: "(Jahrgang)",
    VALIDATON_ERROR: {
      max: "* Der Wert sollte unter {{max}} liegen.",
      min: "* Der Wert sollte über {{min}} liegen.",
    } satisfies Record<string, string>,
    INIT_BY_TYPE: {
      [QueryTermType.Fulltext]: "Volltextsuche",
      [QueryTermType.Title]: "Titel",
    },
    CLEAR_FILTERS_DIALOG: {
      TITLE: "Sind Sie sicher?",
      CONTENT: "Alle Filter zurücksetzen",
      CANCEL: "Abbrechen",
      OK: "Ja",
    },
  },
  SEARCH_RESULTS: {
    TITLE: "Suchresultate",
    NO_RESULTS: "Keine Resultate",
    DETAILED_SEARCH: "Zur detaillierten Suche",
    INFO: "{{total}} Resultate, Seite {{currentPage}} von {{totalPages}}",
  },
  SEARCH_RESULT: {
    VINTAGE: "Jahrgang {{vintage}}.",
    PAGES: "{{pages}} Seiten",
    PAGE: "Seite",
  },
  HOME_PAGE: {
    TOOLTIP: {
      PUB_AMTSBLATT:
        "Das Amtsblatt ist das amtliche Publikationsorgan des Kantons für amtliche Bekanntmachungen und Publikationen. \n\nDie Amtsblätter der Jahre 1949- 2019 sind verfügbar. Neuere Exemplare sind auf der Publikationsplattform des Kanton St.Gallen abrufbar.",
      PUB_ABSTIMMUNGSBROSCHUEREN:
        "In den Abstimmungsbroschüren finden Sie die erläuternden Berichte des Präsidiums des Kantonsrates zu den vergangenen kantonalen Abstimmungen, welche den Stimm- und Wahlberechtigten zur Verfügung gestellt werden. \n\nDie Abstimmungsbroschüren der Jahre 2004- 2019 sind verfügbar. Neuere Exemplare sind auf der Publikationsplattform des Kanton St.Gallen abrufbar.",
      PUB_GESCHAEFTSBERICHTE_REGIERUNG:
        "Mit dem Geschäftsbericht gibt die Regierung Auskunft über ihre Tätigkeit und die der sieben Departemente sowie der Staatskanzlei. Der Geschäftsbericht erscheint einmal im Jahr. \n\nDie Geschäftsberichte der Regierung der Jahre 2008-2019 sind verfügbar. Neuere Exemplare sind auf der Publikationsplattform des Kanton St.Gallen abrufbar.",
      PUB_GVP:
        "Publikation ausgewählter Entscheidungen und Urteile der St.Galler Gerichte, die von aktuellem öffentlichen Interesse sind. \n\nDie st.gallische Gerichts- und Verwaltungspraxis der Jahre 1980-2016 ist verfügbar. Das Format wurde im Jahr 2016 eingestellt.",
      PUB_STAATSKALENDER:
        "Der Staatskalender wird von der Staatskanzlei des Kanton St.Gallen verfasst und vermittelt Angaben zu Ansprechpersonen und Verwaltungseinheiten. \n\nDie Staatskalender der Jahre 2006-2021 sind verfügbar. Neuere Exemplare sind auf der Publikationsplattform des Kanton St.Gallen abrufbar.",
      PUB_KANTONSRATSPROTOKOLLE:
        "Das Kantonsratsprotokoll und dessen Beilagen der Jahre 1965-1989 sind verfügbar.\n\nIm Kantonsratsprotokoll im engeren Sinn finden sich die Wortmeldungen und Beschlüsse zu den Geschäften des Kantonsrates aus den Sessionen, der Wortlaut der während der Beratung gestellten Anträge sowie die Bezeichnung der Beratungsgegenstände und -unterlagen. Im weiteren Sinn gehören zum Kantonsratsprotokoll auch die Beratungsunterlagen, also die Geschäftsdokumente\n\nSeit der Amtsdauer 2008/2012 ist das Kantonsratsprotokoll im Ratsinformationssystem unter www.ratsinfo.sg.ch abrufbar.",
      PUB_REGIERUNGSPROTOKOLLE: "",
      PUB_SCHULBLATT:
        "Das Schulblatt ist das amtliche Publikationsorgan des Bildungsdepartementes für Erlasse, Weisungen und weitere offizielle Mitteilungen. \n\nDie Schulblätter der Jahre 1935-2005 sind verfügbar.",
      PUB_GESETZESSAMMLUNG:
        "Die Gesetzessammlungen der Jahre 1956-2009 sind verfügbar.\n\nDie Gesetzessammlung ist das amtliche Publikationsorgan des Kantons für allgemeinverbindliche Erlasse des Kantonsrates, der Regierung und weiterer Gremien.\n\nSeit dem Herbst 2019 sind die Erlasse nur noch unter www.gesetzessammlung.sg.ch bzw. www.gallex.ch abrufbar.",
      LINK_TRANSKRIBUS:
        "Die an den wöchentlichen Sitzungen der Regierung gefällten Beschlüsse sind samt den zugehörigen Erwägungen in den sogenannten Verhandlungsprotokollen festgehalten. Die Regierungsratsprotokolle der Jahre 1803 - 1831 sind unter transkribus.org verfügbar.",
    },
  },
  EXTERNAL_NAVIGATION: {
    TOOLTIP: "Öffnet <b>{{title}}</b> in einem neuen Tab",
    DIALOG: {
      TITLE: `Sie sind dabei, Retrodigital zu verlassen.`,
      CONTENT: `Möchten Sie fortfahren und zu einer externen Seite (Transkribus.org) wechseln?

Durch das Öffnen des Links verlassen Sie die Website von Retrodigital.`,
      CANCEL: "Abbrechen",
      OK: "Weiter zur externen Seite",
    },
  },
  PAGES_NAV: {
    ABOUT_US: "Über Retrodigital",
    LEGAL_INFO: "Rechtliche Hinweise",
    HELP: "Hilfe",
    IMPRESSUM: "Impressum",
  },
  LIBRARY: {
    TITLE: "Digitalisate",
    NO_SOURCE: "Das PDF kann nicht geladen werden.",
    NAVIGATION: {
      PAGES: "S. {{start}}-{{end}}",
      PAGE: "S. {{page}}",
      SHOW_ATTACHMENT: "Beilage anzeigen",
      SHOW_PARENT: "Ursprungsdokument anzeigen",
    },
  },
  DOWNLOAD_BTN: "PDF Herunterladen",
  DATABASE: {
    NOT_KNOWN: "nbk.",
    BTN_SOURCE: "Source",
  },
  PDF_VIEW: {
    SEARCH: "Suche",
    TOOLTIP: {
      PAGE_PREV: "Vorherige Seite",
      PAGE_NEXT: "Nächste Seite",
      ZOOM_IN: "Vergrössern",
      ZOOM_OUT: "Verkleinern",
      ZOOM_AUTO: "Auf Seitengrösse anpassen",
      FIND_PREV: "Vorheriger Treffer",
      FIND_NEXT: "Nächster Treffer",
    },
  },
  EDITOR: {
    TITLE: "Editor",
    HEADERS: {
      TITLE: "Titel",
      CONTENT: "Inhalt",
      DOCUMENT: "Dokument",
    },
    DOC_UPLOAD: {
      DROP_LABEL: "Datei zum Hochladen hineinziehen",
      TITLE: "Dokument und/oder Titelbild ersetzen",
      UPLOAD_BTN: "Ersetze",
      UPLOAD_AND_SAVE_BTN: "Speichern und Ersetzen",
    },
    DIALOGS: {
      LEAVE_DIRTY: {
        TITLE: "Ungesicherte änderungen",
        MESSAGE:
          "Sie haben änderungen an diesem Element vorgenommen, welche noch nicht gesichert wurden. Sind Sie sicher, dass Sie diese verwerfen wollen?",
        BTN_CANCEL: "Abbrechen",
        BTN_LEAVE: "Verwerfen",
        BTN_SAVE: "Speichern und fortfahren",
      },
      VERSION_CHANGED: {
        TITLE: "Der Inhalt dieses Elements wurde geändert, nachdem Sie das Element geöffnet hatten.",
        MESSAGE: "Möchten Sie die neuste Version laden und Ihre Änderungen verwerfen?",
        LOCAL_VERSION: "Lokale Version",
        SERVER_VERSION: "Server Version",
        LOCAL_SESSION_ID: "Lokale Sitzung-ID",
        SERVER_SESSION_ID: "Server Sitzung-ID",
        BTN_RELOAD: "Neu laden",
      },
      LOCK_FAILED: {
        TITLE: "Sperre fehlgeschlagen",
        THIS_NODE_EDITED_BY_USER: "Dieses Element wird momentan von '{{user}}' bearbeitet.",
        THIS_NODE_EDITED_BY_UNKNOWN: "Dieses Element kann nicht geladen werden.",
        BTN_CLOSE: "Element schliessen",
        BTN_RETRY: "Wiederholen",
        BTN_FORCE: "Erzwingen",
      },
      CONFIRM_NODE_DELETE: {
        TITLE: "Element Löschen",
        MESSAGE: "Sind Sie sicher, dass Sie dieses Element löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
        BTN_CANCEL: "Abbrechen",
        BTN_CONFIRM: "Löschen",
      },
      CONFIRM_DOC_UPLOAD: {
        TITLE: "*Dokument ersetzen",
        MESSAGE: "*Sind Sie sicher, dass Sie das aktuelle Dokument durch den hochgeladenen Inhalt ersetzen möchten?",
        BTN_CANCEL: "Abbrechen",
        BTN_CONFIRM: "Ersetzen",
      },
      NODE_DELETE_FAILED: {
        TITLE: "Das Element kann nicht gelöscht werden",
        PARENT_NODE: "Das beinhaltete Element '{{node}}' ",
        THIS_NODE: "Dieses Element ",
        EDITED_BY_USER: "wird momentan von '{{user}}' bearbeitet.",
        EDITED_BY_UNKNOWN: "wird momentan von einem anderen Benutzer bearbeitet.",
        BTN_CANCEL: "Abbrechen",
        BTN_RETRY: "Wiederholen",
        BTN_FORCE: "Löschen erzwingen",
      },
    },
    MESSAGES: {
      NO_NODE: "Bitte wählen Sie ein Element aus der Navigation links",
      LIMITED_NODE: "Der Inhalt dieses Elements ist nicht editierbar.",
    },
    TREE_NAVIGATION: {
      BTN_TIP_RELOAD: "Navigation neu laden",
      BTN_TIP_GOTOHOME: "Springe zu aktuellem Element",
      BTN_TIP_NEWNODE: "Neues Element",
    },
    TOOLBAR: {
      HIERARCHY_LEVEL: "Hierarchiestufe",
      NODE_STATE: "Status",
    },
    REFERENCES: {
      TEXT: "Beschriftungstext",
      HREF: "URL",
    },
    ACTIONS: {
      TOOLTIPS: {
        ADD_TEXT: "Textblock hinzufügen",
        ADD_IMAGEGROUP: "Bildgruppe hinzufügen",
        ADD_TABLE: "Tabellenblock hinzufügen",
        ADD_INFOBOX: "Infobox hinzufügen",
        MERGE_TABLES: "Tabellen kombinieren",
      },
    },
    INSPECTOR: {
      EDITORS: {
        DOCUMENT: {
          TITLE: "Dokument",
          PROPS: {
            TITLE: "Titel",
            PDF_OFFSET: "Erste Seite in PDF",
            PAGE_COUNT: "Anzahl der Seiten",
            PERIOD_START: "Startdatum",
            PERIOD_END: "Enddatum",
            TOPICS: "Themen",
            CITATION_REFERENCE: "Abkürzung der Reihe (Zitierweise)",
            SESSION_MONTH: "Session / Monat",
          },
        },
        CHAPTER: {
          TITLE: "Kapitel bearbeiten",
          PROPS: {
            TITLE: "Titel",
            START_PAGE: "Erste Seite",
            TOPICS: "Themen",
          },
        },
        TEXT: {
          TITLE: "Tag Hinzufügen",
          ADD_ENDNOTE: "Neue Endnote hinzufügen",
          ADD_PERSON_REF: "Neue Person hinzufügen",
          ADD_NODE_REF: "Neue Interne Referenz hinzufügen",
          ADD_EXTERNAL_REF: "Neue Externe Referenz hinzufügen",
          ADD_ITALIC: "Neuen kursiver Text hinzufügen",
          ADD_CORRECTION: "Neue Korrektur hinzufügen",
          ADD_UPDATE: "Neues Update hinzufügen",
        },
        ENDNOTE: {
          TITLE: "Endnote",
          FIELD_CONTENT: "Inhalt",
        },
        NODE_REF: {
          TITLE: "Interne Referenz",
          BTN_EDIT: "Element auswählen",
          DIALOG: {
            TITLE: "Hierarchieansicht",
            BTN_CANCEL: "Abbrechen",
            BTN_CONFIRM: "Element auswählen",
          },
        },
        EXTERNAL_REF: {
          TITLE: "Externe Referenz",
          FIELD_HREF: "Link",
        },
        ITALIC: {
          TITLE: "Italic",
        },
        CORRECTION: {
          TITLE: "Correction",
        },
        UPDATE: {
          TITLE: "Update",
        },
        IMAGEGROUP: {
          TITLE: "Bildergruppe",
          FIELD_CAPTION: "Beschriftung",
          BTN_UPLOAD: "Hochladen",
          UPLOAD_AREA: "Bild hier hinziehen",
          TOOLTIP_MOVE: "In ein anderes Kapitel verschieben",
          MOVE_FAILED: "Bild kann nicht verschoben werden, bitte zuerst speichern",
        },
        IMG: {
          TITLE: "Bild",
          FIELD_ICON: "Ikone",
          FIELD_TITLE: "Beschriftung",
          TOOLTIP_MOVE: "In ein anderes Kapitel verschieben",
        },
        TABLE: {
          TITLE: "Tabelle Bearbeiten",
          FIELD_CAPTION: "Beschriftung",
          TOOLTIPS: {
            ADD_COLUMN: "Spalte Hinzufügen",
            REMOVE_COLUMN: "Spalte Löschen",
            ADD_ROW: "Zeile Hinzufügen",
            REMOVE_ROW: "Zeile Löschen",
          },
        },
      },
    },
  },
  PAGES: "Seiten",
  PAGE: "Seite",
  TOASTS: {
    NO_ID_TO_MOVE: "Der Block, den Sie verschieben möchten, hat noch keine ID. Bitte speichern Sie zuerst",
    EDITOR_SAVE_ERROR: "Das Element konnte nicht gespeichert werden",
    IMG_IS_NEED_TO_BE_SELECTED: "Bitte wählen Sie nur ein Bild für den Upload aus",
    SAVING_FAILED: "Speichern fehlgeschlagen",
    TRY_UNLOCK_WARNING: "Das Element kann nicht entsperrt werden",
    COULD_NOT_LOAD_NODE: "Das Element konnte nicht geladen werden",
    INVALID_HIERARCHY_OPERATION: "Ungültige Hierarchieoperation",
    NO_NEW_LEVEL_CREATED: "Es konnte keine neue Hierarchieebene erstellt werden",
    SEARCH_FAILED: "Suche fehlgeschlagen",
    TOKEN_LOGIN_FAILED: "Die Anmeldung ist fehlgeschlagen.",
    TOKEN_LOGIN_SUCCESS: "Die Anmeldung war erfolgreich.",
  },
  FOOTER: {
    COPYRIGHT_LONG: "© Kanton St.Gallen",
  },
  DIALOGS: {
    MISSING_ROLE: {
      TITLE: "Ungenügende Zugriffsrechte",
      ROLE_REQUIRED: "Die folgenden Rollen sind erforderlich, um auf diese Seite zuzugreifen:",
      BTN_LOGIN: "Melden Sie sich als anderen Benutzer an",
      BTN_BACK: "Zur Startseite",
      BTN_BACK_MODAL: "Abbrechen",
    },
  },
};
